import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { LayoutOptions } from '@base/shared';
import type { Shell } from './choice.ts';

/**
 * Lugares da moldura que a tela aberta preenche (ADR 0128): o painel do item escolhido no layout
 * "Três colunas" e a faixa de botões do layout "Planilha". A moldura só mostra o lugar quando a
 * tela o preenche; nos outros layouts ele não existe e a tela mostra o conteúdo no próprio corpo.
 *
 * Dois contextos: o da tela (layout, ajustes e o jeito de preencher, que nunca muda de identidade)
 * e o da moldura (o conteúdo). Assim preencher um lugar não faz a tela renderizar de novo.
 */
export type SlotName = 'inspector' | 'ribbon';
type SlotSetter = (name: SlotName, node: ReactNode | undefined) => void;

interface ScreenSide {
  shell: Shell;
  options: LayoutOptions;
  set: SlotSetter;
}

const ScreenContext = createContext<ScreenSide | null>(null);
const ContentContext = createContext<Partial<Record<SlotName, ReactNode>>>({});

export function ShellProvider({
  shell,
  options,
  children,
}: {
  shell: Shell;
  options: LayoutOptions;
  children: ReactNode;
}) {
  const [content, setContent] = useState<Partial<Record<SlotName, ReactNode>>>({});
  const set = useCallback<SlotSetter>(
    (name, node) => setContent((c) => (c[name] === node ? c : { ...c, [name]: node })),
    [],
  );
  const screen = useMemo(() => ({ shell, options, set }), [shell, options, set]);
  return (
    <ScreenContext.Provider value={screen}>
      <ContentContext.Provider value={content}>{children}</ContentContext.Provider>
    </ScreenContext.Provider>
  );
}

/** O layout e os ajustes dele, para a tela se arrumar (fase 4 do ADR 0128). */
export function useShell(): { shell: Shell; options: LayoutOptions } | null {
  const c = useContext(ScreenContext);
  return c ? { shell: c.shell, options: c.options } : null;
}

export function slotExists(shell: Shell, name: SlotName): boolean {
  return (
    (name === 'inspector' && shell === 'inspector') || (name === 'ribbon' && shell === 'sheet')
  );
}

/**
 * A tela põe `node` num lugar da moldura enquanto estiver aberta. Devolve se o lugar existe no
 * layout atual: quando não existe, a tela mostra o conteúdo no próprio corpo.
 */
export function useShellSlot(name: SlotName, node: ReactNode | undefined): boolean {
  const c = useContext(ScreenContext);
  const exists = !!c && slotExists(c.shell, name);
  const set = c?.set;
  useEffect(() => {
    if (!exists || !set) return;
    set(name, node);
  }, [exists, set, name, node]);
  useEffect(() => {
    if (!exists || !set) return;
    return () => set(name, undefined);
  }, [exists, set, name]);
  return exists;
}

/** O conteúdo que a tela pôs no lugar, ou null (só a moldura lê). */
export function useSlotContent(name: SlotName): ReactNode | null {
  return useContext(ContentContext)[name] ?? null;
}
